// The sign-in screen. Three views inside one overlay:
//   main   — Log in / Register (email + password) + optional "Continue with Google"
//   forgot — request a password-reset email
//   reset  — set a new password (arrived from the email link, ?reset=…)
//
// Login identity is the EMAIL. This screen ONLY authenticates — it hands a session
// token back to the bootstrap (game.ts) and the character-select screen
// (charselect.ts) takes over from there (pick/create a hero, My Account). Every
// success path funnels to a single handler:
//   login / register / reset / google → onAuthed(token)
// The token is what the WebSocket + the REST endpoints authenticate with.

import { $ } from "./dom.ts";

export type SigninHandlers = {
  onAuthed: (token: string) => void;
};

const MIN_PASSWORD = 8;

async function postJson(path: string, body: unknown): Promise<any> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || "something went wrong");
  return data;
}

// Set by initSignin so the standalone exports below (called from game.ts) can drive
// the form after it's wired.
let api: {
  showMain: (message?: string) => void;
  showReset: (token: string) => void;
  note: (message: string, kind?: "ok" | "err") => void;
} | null = null;

export function initSignin(handlers: SigninHandlers) {
  let mode: "login" | "register" = "login";
  let googleOn = false;

  const authTitle = $("#authTitle");
  const authMain = $("#authMain");
  const forgotView = $("#forgotView");
  const resetView = $("#resetView");

  const tabLogin = $<HTMLButtonElement>("#tabLogin");
  const tabRegister = $<HTMLButtonElement>("#tabRegister");
  const googleBtn = $<HTMLButtonElement>("#googleBtn");
  const oauthOr = $("#oauthOr");
  const emailInput = $<HTMLInputElement>("#email");
  const passwordInput = $<HTMLInputElement>("#password");
  const authnote = $("#authnote");
  const err = $("#autherr");
  const joinBtn = $<HTMLButtonElement>(".join");
  const forgotLink = $<HTMLAnchorElement>("#forgotLink");

  const forgotEmail = $<HTMLInputElement>("#forgotEmail");
  const forgotErr = $("#forgoterr");
  const forgotBtn = $<HTMLButtonElement>("#forgotBtn");
  const forgotBack = $<HTMLAnchorElement>("#forgotBack");

  const newPassword = $<HTMLInputElement>("#newPassword");
  const resetErr = $("#reseterr");
  const resetBtn = $<HTMLButtonElement>("#resetBtn");
  let resetToken = "";

  // ---- view + mode -------------------------------------------------------

  function view(v: "main" | "forgot" | "reset") {
    authMain.classList.toggle("hide", v !== "main");
    forgotView.classList.toggle("hide", v !== "forgot");
    resetView.classList.toggle("hide", v !== "reset");
  }

  function applyGoogle() {
    const show = googleOn && !authMain.classList.contains("hide");
    googleBtn.classList.toggle("hide", !show);
    oauthOr.classList.toggle("hide", !show);
  }

  function setMode(m: "login" | "register") {
    mode = m;
    forgotLink.classList.toggle("hide", m !== "login");
    tabLogin.classList.toggle("sel", m === "login");
    tabRegister.classList.toggle("sel", m === "register");
    authTitle.textContent = "Enter the world";
    joinBtn.textContent = m === "login" ? "Log in" : "Create account";
    passwordInput.autocomplete = m === "login" ? "current-password" : "new-password";
    err.textContent = "";
    applyGoogle();
  }
  tabLogin.onclick = () => setMode("login");
  tabRegister.onclick = () => setMode("register");

  function clearNotes() {
    err.textContent = "";
    authnote.textContent = "";
    authnote.classList.add("hide");
  }

  // ---- submit (login / register) -----------------------------------------

  async function submit() {
    clearNotes();
    const email = emailInput.value.trim();
    const password = passwordInput.value;
    if (!email || !password) { err.textContent = "enter your email and password"; return; }

    joinBtn.disabled = true;
    try {
      if (mode === "register") {
        // If a captcha is required but its widget hasn't loaded/solved yet,
        // sending an empty token would fail server-side with a misleading
        // "captcha check failed" — wait rather than submit blind.
        const captcha = turnstileToken();
        if (turnstileRequired && !captcha) {
          err.textContent = "just a moment — finishing the security check…";
          joinBtn.disabled = false;
          return;
        }
        const { token } = await postJson("/api/register", { email, password, turnstile: captcha });
        handlers.onAuthed(token);
      } else {
        const { token } = await postJson("/api/login", { email, password });
        handlers.onAuthed(token);
      }
      hideSignin();
    } catch (e: any) {
      err.textContent = e.message;
      joinBtn.disabled = false;
      resetTurnstile();
    }
  }
  joinBtn.onclick = submit;

  // ---- forgot / reset ----------------------------------------------------

  forgotLink.onclick = (e) => {
    e.preventDefault();
    forgotEmail.value = emailInput.value.trim();
    forgotErr.textContent = "";
    view("forgot");
    forgotEmail.focus();
  };
  forgotBack.onclick = (e) => { e.preventDefault(); view("main"); setMode("login"); };

  forgotBtn.onclick = async () => {
    forgotErr.textContent = "";
    const email = forgotEmail.value.trim();
    if (!email) { forgotErr.textContent = "enter your email"; return; }
    forgotBtn.disabled = true;
    try {
      await postJson("/api/auth/forgot", { email });
      view("main");
      setMode("login");
      api?.note("If that email is registered, a reset link is on its way.", "ok");
    } catch (e: any) {
      forgotErr.textContent = e.message;
    } finally {
      forgotBtn.disabled = false;
    }
  };

  resetBtn.onclick = async () => {
    resetErr.textContent = "";
    const password = newPassword.value;
    if (password.length < MIN_PASSWORD) {
      resetErr.textContent = `password must be at least ${MIN_PASSWORD} characters`;
      return;
    }
    resetBtn.disabled = true;
    try {
      const { token } = await postJson("/api/auth/reset", { token: resetToken, password });
      handlers.onAuthed(token);
      hideSignin();
    } catch (e: any) {
      resetErr.textContent = e.message;
      resetBtn.disabled = false;
    }
  };

  // ---- Google + Turnstile (both server-gated via /api/auth/config) --------

  googleBtn.onclick = () => { window.location.href = "/api/auth/google/start"; };

  let turnstileWidget: string | null = null;
  let turnstileRequired = false; // server has Turnstile configured (from /api/auth/config)
  function turnstileToken(): string | undefined {
    const ts = (window as any).turnstile;
    if (turnstileWidget && ts) return ts.getResponse(turnstileWidget) || undefined;
    return undefined;
  }
  function resetTurnstile(): void {
    const ts = (window as any).turnstile;
    if (turnstileWidget && ts) ts.reset(turnstileWidget);
  }
  function initTurnstile(siteKey: string): void {
    const box = $("#turnstileBox");
    box.classList.remove("hide");
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.onload = () => {
      const ts = (window as any).turnstile;
      if (ts) turnstileWidget = ts.render(box, { sitekey: siteKey, appearance: "interaction-only" });
    };
    document.head.appendChild(s);
  }

  (async () => {
    try {
      const cfg = await fetch("/api/auth/config").then((r) => r.json());
      googleOn = !!cfg.google;
      applyGoogle();
      if (cfg.turnstile && cfg.turnstileSiteKey) { turnstileRequired = true; initTurnstile(cfg.turnstileSiteKey); }
    } catch {
      /* config is optional — the form works without Google/Turnstile */
    }
  })();

  // ---- keyboard: Enter submits; never leak typing into the game handlers --

  const onEnter = (inp: HTMLInputElement, run: () => void) =>
    inp.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") run();
    });
  for (const inp of [emailInput, passwordInput]) onEnter(inp, submit);
  onEnter(forgotEmail, () => forgotBtn.click());
  onEnter(newPassword, () => resetBtn.click());

  // ---- expose driver for the standalone exports below --------------------

  api = {
    showMain(message?: string) {
      view("main");
      setMode("login");
      clearNotes();
      if (message) err.textContent = message;
    },
    showReset(token: string) {
      resetToken = token;
      resetErr.textContent = "";
      newPassword.value = "";
      view("reset");
      newPassword.focus();
    },
    note(message: string, kind: "ok" | "err" = "ok") {
      view("main");
      if (kind === "err") {
        err.textContent = message;
      } else {
        authnote.textContent = message;
        authnote.classList.remove("hide");
      }
    },
  };

  setMode("login");
  emailInput.focus();
}

/** Hide the sign-in overlay (after a successful auth / on auto-login). */
export function hideSignin(): void {
  ($("#signin") as HTMLElement).style.display = "none";
}

/** Re-show the sign-in overlay on the login view with an optional message (auto-
 *  login failed, session expired, …). */
export function showSignin(message?: string): void {
  const signin = $("#signin") as HTMLElement;
  signin.style.display = "flex";
  const btn = $<HTMLButtonElement>(".join");
  btn.disabled = false;
  api?.showMain(message);
}

/** Show the "set a new password" view, seeded with the token from the reset link. */
export function showResetView(token: string): void {
  ($("#signin") as HTMLElement).style.display = "flex";
  api?.showReset(token);
}

/** Surface a one-off note on the sign-in screen (email verified, OAuth error, …). */
export function showAuthNote(message: string, kind: "ok" | "err" = "ok"): void {
  ($("#signin") as HTMLElement).style.display = "flex";
  api?.note(message, kind);
}

/** Show the full-screen loading overlay while we connect + wait for `spawn`, so
 *  the player never sees the world at the origin before their saved spot loads. */
export function showLoading(message = "Entering the world…"): void {
  const el = $("#loading") as HTMLElement;
  const m = el.querySelector(".loadmsg");
  if (m) m.textContent = message;
  el.classList.add("show");
}

/** Fade the loading overlay out (called once the player is placed). */
export function hideLoading(): void {
  ($("#loading") as HTMLElement).classList.remove("show");
}

/** Screen fade for in-session map transfers (plan 24): a plain black sheet
 *  (#fade, CSS opacity transition) that hides the world teardown/rebuild.
 *  Resolves on transitionend with a timer fallback — transitions are lossy
 *  under tab-blur, and the swap must never wedge on a missed event. */
function fadeTo(show: boolean, ms: number): Promise<void> {
  const el = $("#fade") as HTMLElement;
  el.style.transitionDuration = `${ms}ms`;
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      el.removeEventListener("transitionend", finish);
      resolve();
    };
    el.addEventListener("transitionend", finish);
    setTimeout(finish, ms + 80);
    el.classList.toggle("show", show);
  });
}

/** Fade the screen to black (~250ms). Await before tearing the world down. */
export function fadeOut(ms = 250): Promise<void> {
  return fadeTo(true, ms);
}

/** Fade back in from black once the new world is built + the player placed. */
export function fadeIn(ms = 250): Promise<void> {
  return fadeTo(false, ms);
}

/** Show the "already open in another tab" notice after the server evicted this
 *  session (single active session per character). `onPlayHere` reconnects and
 *  takes the character back into THIS tab. */
export function showDisplaced(onPlayHere: () => void): void {
  const el = $("#displaced") as HTMLElement;
  el.style.display = "flex";
  const btn = $<HTMLButtonElement>("#playhere");
  btn.onclick = () => { el.style.display = "none"; onPlayHere(); };
}

export function hideDisplaced(): void {
  ($("#displaced") as HTMLElement).style.display = "none";
}
